(() => {
  if (window.CsbEditorialMotion) {
    window.CsbEditorialMotion.init(document);
    return;
  }

  // Only static editorial content. Existing spec/FAQ and About animations keep
  // their own controllers; the sticky hero and carousel never enter here.
  const revealSelector = [
    '.csb-story__heading', '.csb-step', '.csb-story__watch-link',
    '.csb-kit__heading', '.csb-kit__art', '.csb-kit__contents > li', '.csb-kit__footer',
    '.csb-material__heading', '.csb-material__footer', '.csb-watch__copy',
    '.csb-video', '.csb-reviews__heading', '.csb-reviews__grid > figure',
    '.csb-reviews__more', '.csb-faq__heading',
    '.csb-conference__intro', '.csb-conference__film-copy',
    '.csb-conference__video-wrap', '.csb-about__portrait', '.csb-about__candid',
    '.csb-product-story__heading', '.csb-product-kit__image',
    '.csb-product-kit__contents > li', '.csb-product-material > .page-width > h2',
    '.csb-product-material__intro', '.csb-product-material__offline',
    '.csb-product-guide-link', '.csb-box-finale__copy', '.csb-box-finale__bottom'
  ].join(',');
  const counterSelector = '[data-csb-counter]';
  const media = matchMedia('(prefers-reduced-motion: reduce)');
  const supportsAnimation = typeof Element.prototype.animate === 'function';
  const completed = new WeakSet();
  const observed = new Set();
  const animations = new Map();
  const counters = new Map();
  const counterTargets = new Map();
  let counterFrame = 0;

  const within = (root, selector) => [
    ...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)
  ];
  const finishCounter = (element) => {
    const counter = counters.get(element);
    if (!counter) return;
    counter.value.textContent = counter.finalText;
    element.dataset.csbCounted = 'true';
    completed.add(element);
    counterObserver?.unobserve(counter.observedElement);
    counterTargets.delete(counter.observedElement);
    counters.delete(element);
  };

  const tickCounters = (now) => {
    counterFrame = 0;
    let playing = false;
    counters.forEach((counter, element) => {
      if (counter.start === null) return;
      if (!element.isConnected || media.matches) {
        finishCounter(element);
        return;
      }
      const progress = Math.max(0, Math.min(1, (now - counter.start) / 1400));
      if (progress === 1) {
        finishCounter(element);
        return;
      }
      const value = Math.floor(counter.target * (1 - Math.pow(1 - progress, 3)));
      if (value !== counter.last) {
        counter.value.textContent = String(value);
        counter.last = value;
      }
      playing = true;
    });
    if (playing) counterFrame = requestAnimationFrame(tickCounters);
  };

  const reveal = (element, delay) => {
    if (completed.has(element)) return;
    completed.add(element);
    if (media.matches || !supportsAnimation) return;
    // A short horizontal movement is reserved for two-column copy blocks.
    const horizontal = element.matches('.csb-conference__film-copy, .csb-faq__heading');
    const animation = element.animate([
      { opacity: 0, transform: horizontal ? 'translateX(-14px)' : 'translateY(18px)' },
      { opacity: 1, transform: 'translate(0, 0)' }
    ], { duration: 760, delay, easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'backwards' });
    animations.set(element, animation);
    animation.onfinish = () => animations.delete(element);
  };

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    let order = 0;
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const element = entry.target;
      observer.unobserve(element);
      observed.delete(element);
      const delay = Math.min(order++ * 75, 225);
      reveal(element, delay);
    });
  }, { threshold: 0.05, rootMargin: '0px 0px 4% 0px' }) : null;

  // Match the existing stats entrance threshold and stagger so counting begins
  // with that animation, rather than completing before the figures are visible.
  const counterObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    let order = 0;
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      counterObserver.unobserve(entry.target);
      const element = counterTargets.get(entry.target);
      if (!counters.has(element)) return;
      counters.get(element).start = performance.now() + Math.min(order++ * 95, 285);
      if (!counterFrame) counterFrame = requestAnimationFrame(tickCounters);
    });
  }, { threshold: 0.2, rootMargin: '0px 0px -5% 0px' }) : null;

  const init = (root = document) => {
    if (!document.body.classList.contains('csb-theme')) return;
    within(root, counterSelector).forEach((element) => {
      if (completed.has(element) || counters.has(element)) return;
      const value = element.querySelector('.csb-counter__value');
      const target = Number(element.dataset.csbCounter);
      if (!value || !Number.isFinite(target) || target < 0) return;
      const finalText = value.textContent;
      if (!counterObserver || media.matches) {
        completed.add(element);
        return;
      }
      const observedElement = element.closest('.csb-spec-reveal') || element;
      counters.set(element, { value, target, finalText, observedElement, last: 0, start: null });
      counterTargets.set(observedElement, element);
      value.textContent = '0';
      counterObserver.observe(observedElement);
    });
    within(root, revealSelector).forEach((element) => {
      if (completed.has(element) || observed.has(element)) return;
      // Leave the content already on screen untouched when a page first opens.
      if (!observer || media.matches || !supportsAnimation || element.getBoundingClientRect().top < innerHeight) {
        completed.add(element);
        return;
      }
      observed.add(element);
      observer.observe(element);
    });
  };

  const dispose = (root) => {
    const belongs = (element) => element === root || root.contains(element);
    observed.forEach((element) => {
      if (!belongs(element)) return;
      observer?.unobserve(element);
      observed.delete(element);
    });
    animations.forEach((animation, element) => {
      if (!belongs(element)) return;
      animation.cancel();
      animations.delete(element);
    });
    counters.forEach((counter, element) => {
      if (belongs(element)) finishCounter(element);
    });
  };

  const finishMotion = () => {
    animations.forEach((animation) => animation.cancel());
    animations.clear();
    counters.forEach((counter, element) => finishCounter(element));
    if (counterFrame) cancelAnimationFrame(counterFrame);
    counterFrame = 0;
    observed.forEach((element) => completed.add(element));
    observed.clear();
    observer?.disconnect();
    counterObserver?.disconnect();
  };
  const preferenceChanged = () => { if (media.matches) finishMotion(); };
  if (media.addEventListener) media.addEventListener('change', preferenceChanged);
  else media.addListener(preferenceChanged);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => dispose(event.target));
  document.addEventListener('focusin', (event) => {
    animations.forEach((animation, element) => {
      if (!element.contains(event.target)) return;
      animation.cancel();
      animations.delete(element);
    });
  });
  window.addEventListener('beforeprint', finishMotion);
  window.CsbEditorialMotion = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
